import React, { useRef, useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material'
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogContent,
  Button,
  Box,
  Tooltip,
  Badge,
  Stack,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { BootstrapDialogTitle } from 'components/generic/modals/dialog-modal'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import { connectBlocksFrom } from '@uploadcare/blocks/abstract/connectBlocksFrom.js'
import st from './file-uploader-styles.module.css'
import { getAvatarFile, getDownloadUrl } from './utils'

connectBlocksFrom('https://unpkg.com/@uploadcare/blocks@0.19.0/web/blocks-browser.min.js')

const FileUploader = ({ filesData, disabled, viewOnly, onUploadFiles, onRemoveFiles, tooltipMsg, buttonText }) => {
  const { loading } = useSelector(state => state.loading)
  const [showAttachedFilesModal, setShowAttachedFilesModal] = useState(false)
  const dataOutputRef = useRef()
  const uploaderBtnRef = useRef()
  const [files, setFiles] = useState(filesData)
  const [newFiles, setNewFiles] = useState([])
  const [fileToDelete, setFileToDelete] = useState({})

  const handleUploaderEvent = useCallback(
    e => {
      const { data } = e.detail
      onUploadFiles(data.groupData?.uuid)
      setNewFiles([
        ...data.files.map(f => ({
          name: f.originalFilename,
          uuid: f.uuid,
          url: f.cdnUrl,
          createdAt: data.groupData?.createdAt,
        })),
      ])
    },
    [onUploadFiles],
  )

  useEffect(() => {
    setFiles(filesData)
  }, [filesData])

  useEffect(() => {
    const uuids = new Set(files.map(d => d.uuid))
    setFiles([...files, ...newFiles.filter(f => !uuids.has(f.uuid))])
  }, [newFiles])

  // Upload button from uploadcare library is hide
  // it is triggered only, not clicked directly
  useEffect(() => {
    uploaderBtnRef.current?.children[0].setAttribute('hidden', true)
  }, [])

  useEffect(() => {
    const el = dataOutputRef.current
    el.addEventListener('lr-data-output', handleUploaderEvent)
    return () => {
      el.removeEventListener('lr-data-output', handleUploaderEvent)
    }
  }, [handleUploaderEvent])

  const handleCloseModal = () => setShowAttachedFilesModal(false)

  const handleOpenUploader = () => uploaderBtnRef.current?.children[0]?.children[0]?.children[0]?.click()

  const handleDeleteFile = fileUUID => {
    const filesUpdated = files.filter(x => x.uuid !== fileUUID)
    setFiles(filesUpdated)
    onRemoveFiles(filesUpdated)
  }

  const renderFiles = () => (
    <List sx={{ width: '100%' }} dense>
      {files.map((file, index) => {
        const labelId = `list-label-${index}`
        return (
          <div key={labelId}>
            <ListItem
              key={labelId}
              sx={{ pr: 10, py: 1 }}
              disablePadding
              secondaryAction={
                <Box>
                  <IconButton
                    disabled={loading || viewOnly}
                    edge="end"
                    aria-label="delete"
                    onClick={() => setFileToDelete(file)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    disabled={loading}
                    edge="end"
                    aria-label="download"
                    sx={{ ml: 2 }}
                    href={getDownloadUrl(file?.uuid)}
                    target="_blank"
                  >
                    <CloudDownloadIcon />
                  </IconButton>
                </Box>
              }
            >
              <ListItemIcon>{getAvatarFile(file?.name, file?.url)}</ListItemIcon>
              <ListItemText id={labelId} primary={file?.name} />
            </ListItem>
            <Divider />
          </div>
        )
      })}
    </List>
  )

  const renderUploaderButton = () => (
    <Box
      className="lr-wgt-common"
      sx={{
        '& lr-progress-bar-common': {
          // set display to none, because otherwise this causes an unclickable line in our UI
          display: 'none',
        },
      }}
    >
      <lr-file-uploader-regular
        style={{
          '--cfg-pubkey': `"${process.env.REACT_APP_UPLOADCARE_KEY}"`,
        }}
        class={st.uploaderCfg}
        ref={uploaderBtnRef}
      >
        <lr-data-output
          use-console
          ref={dataOutputRef}
          use-event
          use-group
          style={{
            '--cfg-pubkey': `"${process.env.REACT_APP_UPLOADCARE_KEY}"`,
          }}
          class={st.uploaderCfg}
          onEvent={handleUploaderEvent}
        />
      </lr-file-uploader-regular>
    </Box>
  )

  const renderActionButtons = () => (
    <Box
      py={1}
      display="flex"
      flexDirection="row-reverse"
      sx={{ width: '100%', margin: 'auto' }}
      justifyContent="flex-start"
      paddingRight={2}
    >
      <LoadingButton
        disabled={viewOnly}
        color="success"
        size="large"
        autoFocus
        variant="contained"
        onClick={handleOpenUploader}
        sx={{ ml: 1 }}
        loading={loading}
      >
        Upload File
      </LoadingButton>
      <Button disabled={loading} variant="outlined" color="success" size="large" autoFocus onClick={handleCloseModal}>
        Done
      </Button>
    </Box>
  )

  return (
    <>
      <AlertModal
        open={!!fileToDelete?.uuid}
        modalType="close"
        title="Remove Attachment"
        question="Are you sure you want to remove this attachment?"
        description={
          <Stack direction="row">
            <ListItemIcon>{getAvatarFile(fileToDelete?.name, fileToDelete?.url)}</ListItemIcon>
            <ListItemText primary={fileToDelete?.name} />
          </Stack>
        }
        confirmLabel="YES, REMOVE"
        cancelLabel="GO BACK"
        onClose={() => {
          setFileToDelete({})
        }}
        onSubmit={() => {
          handleDeleteFile(fileToDelete?.uuid)
          setFileToDelete({})
        }}
      />
      <Dialog
        open={showAttachedFilesModal}
        onClose={handleCloseModal}
        aria-labelledby="confirm-dialog-title"
        sx={{
          '& .MuiDialog-paper': {
            width: 500,
            height: 'auto',
          },
        }}
      >
        <BootstrapDialogTitle id="attached-dialog-title" onClose={handleCloseModal}>
          Attached Files
        </BootstrapDialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="left" alignItems="center" flex={1}>
            {renderFiles()}
          </Box>
        </DialogContent>
        {renderActionButtons()}
      </Dialog>
      <Tooltip title={disabled ? tooltipMsg : ''}>
        <>
          {buttonText ? (
            <Button
              variant="text"
              aria-label="attach-file"
              disabled={disabled}
              onClick={() => setShowAttachedFilesModal(true)}
              startIcon={
                <Badge badgeContent={files.length > 0 ? files.length : 0} color="primary">
                  <AttachFileIcon fontSize="small" />
                </Badge>
              }
            >
              {buttonText}
            </Button>
          ) : (
            <IconButton aria-label="attach-file" disabled={disabled} onClick={() => setShowAttachedFilesModal(true)}>
              <Badge badgeContent={files.length > 0 ? files.length : 0} color="primary">
                <AttachFileIcon />
              </Badge>
            </IconButton>
          )}
        </>
      </Tooltip>
      {renderUploaderButton()}
    </>
  )
}

FileUploader.propTypes = {
  disabled: PropTypes.bool,
  tooltipMsg: PropTypes.string,
  viewOnly: PropTypes.bool,
  onUploadFiles: (props, propName, componentName) => {
    if (props.viewOnly) return null
    if (typeof props[propName] !== 'function') {
      return new Error(`Invalid prop ${propName} supplied to ${componentName}. Function is required.`)
    }
    return null
  },
  onRemoveFiles: (props, propName, componentName) => {
    if (props.viewOnly) return null
    if (typeof props[propName] !== 'function') {
      return new Error(`Invalid prop ${propName} supplied to ${componentName}. Function is required.`)
    }
    return null
  },
  filesData: PropTypes.array,
  buttonText: PropTypes.string,
}

FileUploader.defaultProps = {
  filesData: [],
  disabled: false,
  tooltipMsg: '',
  viewOnly: false,
  onUploadFiles: () => null,
  onRemoveFiles: () => null,
  buttonText: '',
}

export default FileUploader
